/**
 * Resolve @scope.path expressions and recursively process objects.
 *
 * Supports:
 *  - Literal strings, numbers, booleans, null
 *  - "@scope.path" → reads from execution context
 *  - Objects/arrays: recursively resolved
 *
 * Sandboxed: no eval, no template strings, no function calls.
 */

import type { ExecutionContext } from "../context/execution-context";
import { parsePath, readPath } from "./path";

function shouldUseFallbackValue(resolved: unknown): boolean {
  return resolved === null || resolved === undefined || resolved === "";
}

export function resolveValue(value: unknown, ctx: ExecutionContext): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return resolveString(value, ctx);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.map((item) => resolveValue(item, ctx));
  if (typeof value === "object") {
    const bindingResolved = resolveBindingObject(
      value as Record<string, unknown>,
      ctx,
    );
    if (bindingResolved.resolved) {
      return bindingResolved.value;
    }

    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = resolveValue(v, ctx);
    }
    return out;
  }
  return value;
}

function resolveString(str: string, ctx: ExecutionContext): unknown {
  if (!str.startsWith("@")) return str;
  const parsed = parsePath(str);
  if (!parsed) return undefined;
  const root = ctx.getScope(parsed.scope);
  if (root === undefined) return undefined;
  return readPath(root, parsed.segments);
}

function resolveBindingObject(
  value: Record<string, unknown>,
  ctx: ExecutionContext,
): { resolved: true; value: unknown } | { resolved: false } {
  const binding = Object.prototype.hasOwnProperty.call(value, "$bind")
    ? value.$bind
    : Object.prototype.hasOwnProperty.call(value, "ref")
      ? value.ref
      : undefined;

  if (typeof binding !== "string" || !binding.trim()) {
    return { resolved: false };
  }

  const boundValue = resolveBindingReference(binding, ctx);

  if (
    shouldUseFallbackValue(boundValue) &&
    Object.prototype.hasOwnProperty.call(value, "fallback")
  ) {
    return { resolved: true, value: resolveValue(value.fallback, ctx) };
  }

  return { resolved: true, value: boundValue };
}

function resolveBindingReference(binding: string, ctx: ExecutionContext): unknown {
  const normalized = binding.trim();
  if (!normalized) return undefined;

  const pathExpr = normalized.startsWith("@") ? normalized : `@${normalized}`;
  const parsed = parsePath(pathExpr);
  if (!parsed) return undefined;

  const root = ctx.getScope(parsed.scope);
  if (root === undefined) return undefined;

  return readPath(root, parsed.segments);
}

export function resolveRecord(
  input: Record<string, unknown> | undefined,
  ctx: ExecutionContext,
): Record<string, unknown> {
  if (!input) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    out[k] = resolveValue(v, ctx);
  }
  return out;
}
